'use client';

import { FC, ReactNode } from 'react';
import { css } from '@bts-web/utils-style-engine';
import { AssetImage } from '../../../../common/components/AssetImage/AssetImage.use-client';
import { AssetImageComponentProps } from '../../../../common/types/components';
import { EntryRightIcon } from './EntryRightIcon';
import {
  FilledArrowRight,
  RegularCheckmark,
} from '../../../../common/svgIcons';

export type ITransferPickerEntryType =
  | 'pickerParentWithValue'
  | 'entryWithActiveState';

interface BaseTransferEntityPickerEntryProps {
  title: string;
  subtitle?: string | null;
  heading?: string;
  type: ITransferPickerEntryType;
  onPick?: () => void;
  endInfoMain?: string;
  endInfoSub?: string;
  disabled?: boolean;
}

interface WithImageProps {
  imageUrl: AssetImageComponentProps['src'] | null | undefined;
  iconElement?: never;
}

interface WithIconProps {
  imageUrl?: never;
  iconElement?: ReactNode;
}

type DisplayOptionsProps = WithImageProps | WithIconProps;

interface WithListProps {
  active: boolean;
  withinList: boolean;
}

interface WithoutListProps {
  active?: never;
  withinList?: never;
}

type TransferEntityPickerEntryProps = BaseTransferEntityPickerEntryProps &
  DisplayOptionsProps &
  (WithListProps | WithoutListProps);

const TransferEntityPickerEntry: FC<TransferEntityPickerEntryProps> = ({
  title,
  type,
  imageUrl,
  iconElement,
  onPick,
  heading,
  subtitle,
  endInfoMain,
  endInfoSub,
  active,
  withinList,
  disabled,
}) => {
  const ElementNode = withinList ? 'li' : 'div';

  return (
    <ElementNode
      role="option"
      aria-label={`${title}`}
      aria-selected={type === 'pickerParentWithValue'}
    >
      <button
        aria-label={`change option for ${title}`}
        className={css({
          display: 'flex',
          alignItems: 'center',
          minHeight: '80px',
          width: '100%',
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          textAlign: 'initial',

          ...(disabled
            ? {
                backgroundColor: 'var(--colors-neutrals-fill_disabled)',
                boxShadow:
                  'var(--spacing-medium) 0 0 0 var(--colors-neutrals-fill_disabled), calc(var(--spacing-medium) * -1) 0 0 0 var(--colors-neutrals-fill_disabled)',
                cursor: 'default',
              }
            : {}),
        })}
        onClick={onPick}
        disabled={disabled}
      >
        <div
          className={css({
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            flex: 1,
            gap: 'extra_small_2',
            paddingTop: 'medium',
            paddingBottom: 'medium',
          })}
        >
          {heading ? (
            <div
              className={css({
                color: 'neutrals.text_secondary',
                fontSize: '11px',
                lineHeight: '12px',
              })}
            >
              {heading}
            </div>
          ) : null}

          <div
            className={css({
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              flex: 1,
              gap: 'medium',
            })}
          >
            {/* Display either imageUrl or iconElement but not both */}
            {imageUrl ? (
              <AssetImage
                src={imageUrl}
                alt={`fallback for ${title}`}
                width={40}
                height={40}
              />
            ) : iconElement ? (
              <div
                className={css({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                })}
              >
                {iconElement}
              </div>
            ) : null}

            <div
              className={css({
                justifyContent: 'space-between',
                gap: 'extra_small_3',
                flex: 1,
              })}
            >
              <h3
                className={css({
                  fontSize: 'title.medium_medium',
                  fontWeight: 'title.medium_medium',
                  lineHeight: 'title.medium_medium',
                  letterSpacing: 'title.medium_medium',
                  paddingBottom: 'extra_small_3',
                  margin: 0,
                  marginInlineStart: 'auto',
                  ...(disabled
                    ? {
                        color: 'neutrals.text_secondary',
                      }
                    : {}),
                })}
              >
                {title}
              </h3>

              {subtitle && (
                <div
                  className={css({
                    color: 'neutrals.text_secondary',
                    fontSize: 'caption.medium_medium',
                    fontWeight: 'caption.medium_medium',
                    letterSpacing: 'caption.medium_medium',
                    lineHeight: 'caption.medium_medium',
                    marginInlineStart: 'auto',
                  })}
                >
                  {subtitle}
                </div>
              )}
            </div>

            {endInfoMain || endInfoSub ? (
              <div
                className={css({
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'end',
                  marginInlineStart: 'auto',
                  gap: 'extra_small_3',
                })}
              >
                {endInfoMain ? (
                  <span
                    className={css({
                      color: 'neutrals.text_primary',
                      fontSize: 'label.medium_medium',
                      fontWeight: 'label.medium_medium',
                      lineHeight: 'label.medium_medium',
                      letterSpacing: 'label.medium_medium',
                      ...(disabled
                        ? {
                            color: 'neutrals.text_secondary',
                          }
                        : {}),
                    })}
                  >
                    {endInfoMain}
                  </span>
                ) : null}

                {endInfoSub ? (
                  <span
                    className={css({
                      color: 'neutrals.text_secondary',
                      fontSize: 'caption.small',
                      fontWeight: 'caption.small',
                      lineHeight: 'caption.small',
                      letterSpacing: 'caption.small',
                    })}
                  >
                    {endInfoSub}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {active && withinList ? (
          <EntryRightIcon icon={<RegularCheckmark />} />
        ) : !withinList ? (
          <EntryRightIcon icon={<FilledArrowRight />} />
        ) : null}
      </button>

      <div
        className={css({
          marginInlineStart: '-24px',
          marginInlineEnd: '-24px',
          width: 'calc(100% + 48px)',
          borderBottomWidth: '1px',
          borderBottomColor: 'var(--colors-neutrals-divider)',
        })}
      />
    </ElementNode>
  );
};

export { TransferEntityPickerEntry };
